import { z } from 'zod';

// Define strict Zod validation schemas for frontend configuration payload to prevent XSS and memory corruption.
// Using .passthrough() at all levels to guarantee backward-compatibility for rolling deployments.
const apiConfigurationSchema = z.object({
  timeoutMs: z.number().positive(),
  maxRetries: z.number().nonnegative(),
  cacheMaxAge: z.number().nonnegative(),
  dateFormat: z.string().min(1),
  toastDurationInstitutional: z.number().positive(),
  csrf: z.object({
    cookieName: z.string().min(1),
    headerName: z.string().min(1),
  }).passthrough().optional(),
  fastLookupTimeoutMs: z.number().positive().optional(),
  standardTimeoutMs: z.number().positive().optional(),
  transactionalTimeoutMs: z.number().positive().optional(),
}).passthrough();

const securityConfigurationSchema = z.object({
  sensitivePatterns: z.array(z.string()).optional(),
  maxBroadcastAgeMs: z.number().positive(),
}).passthrough();

const circuitBreakerSchema = z.object({
  threshold: z.number().positive(),
  resetTimeoutMs: z.number().positive(),
  pollingIntervalMs: z.number().positive(),
  halfOpenTimeoutMs: z.number().positive().optional(),
}).passthrough();

const paginationConfigurationSchema = z.object({
  defaultPageSize: z.number().positive(),
}).passthrough();

const elevationConfigurationSchema = z.object({
  rimLightOpacity: z.number().min(0).max(1),
  shadowColor: z.string().optional(),
}).passthrough();

const statusColorsSchema = z.object({
  success: z.string().min(1),
  warning: z.string().min(1),
  error: z.string().min(1),
  neutral: z.string().min(1),
  info: z.string().min(1),
}).passthrough();

const aestheticConfigurationSchema = z.object({
  glassmorphism: z.object({
    enabled: z.boolean(),
    blurIntensity: z.string().min(1),
    transparency: z.number().min(0).max(1),
    noiseAlpha: z.number().min(0).max(1),
    saturation: z.number().nonnegative(),
  }).passthrough(),
  motion: z.object({
    speed: z.string().min(1),
    easingCurve: z.string().min(1),
    staggerDelayMs: z.number().nonnegative(),
    interactiveScale: z.boolean(),
    pageTransitionEnabled: z.boolean(),
    pageTransitionType: z.string().min(1),
  }).passthrough(),
  depth: z.object({
    shadowProfile: z.string().min(1),
    shadowColor: z.string().optional(),
  }).passthrough(),
  layout: z.object({
    uiDensity: z.string().min(1),
    contentMaxWidth: z.string().min(1),
  }).passthrough(),
}).passthrough();

const tableConfigurationSchema = z.object({
  headerPadding: z.string().min(1),
  cellPadding: z.string().min(1),
  headerFontSize: z.string().min(1),
  headerFontWeight: z.string().min(1),
  headerUppercase: z.boolean(),
  headerLetterSpacing: z.string().min(1),
  cellVerticalAlign: z.string().min(1),
  stripeStyle: z.string().min(1),
  borderStyle: z.string().min(1),
  headerVariant: z.string().min(1),
  rowSpacing: z.string().min(1),
  containerStyle: z.string().optional(),
  tableHeaderWrap: z.string().optional(),
  tableCellWrap: z.string().optional(),
  stickyShadow: z.string().optional(),
  collapsibleActions: z.boolean().optional(),
  tableRowContrast: z.boolean().optional(),
  tableRowHoverContrast: z.boolean().optional(),
  tableTextSize: z.string().optional(),
}).passthrough();

const tokenMappingsSchema = z.object({
  padding: z.record(z.string()),
  fontSize: z.record(z.string()),
  fontWeight: z.record(z.string()),
  letterSpacing: z.record(z.string()),
  statusClasses: z.record(z.string()),
}).passthrough();

const themeModeColorsSchema = z.object({
  bgPrimary: z.string().min(1),
  bgSecondary: z.string().min(1),
  bgSurface: z.string().min(1),
  textPrimary: z.string().min(1),
  textSecondary: z.string().min(1),
  sidebarText: z.string().min(1),
  sidebarIcon: z.string().min(1),
  divider: z.string().min(1),
  dividerWidth: z.string().optional(),
  dividerOpacity: z.number().optional(),
  partition: z.string().min(1),
  chartPalette: z.array(z.string()),
  chartGrid: z.string().min(1),
  chartTextColor: z.string().min(1),
  chartFontSize: z.number().positive(),
  chartFontFamily: z.string().min(1),
  chartFontWeight: z.union([z.string(), z.number()]),
  statsContainerBg: z.string().min(1),
  statsContainerOpacity: z.number().min(0).max(1),
  dashboardCardBg: z.string().min(1),
  dashboardCardOpacity: z.number().min(0).max(1),
  selection: z.string().min(1),
  hoverOverlay: z.string().min(1),
  successSurface: z.string().min(1),
  successAccent: z.string().min(1),
  warningSurface: z.string().min(1),
  warningAccent: z.string().min(1),
  errorSurface: z.string().min(1),
  errorAccent: z.string().min(1),
  infoSurface: z.string().min(1),
  infoAccent: z.string().min(1),
  statusBadgeMix: z.string().optional(),
  statusBadgeOpacity: z.number().optional(),
  transitionArrowOpacity: z.number().optional(),
  transitionArrowWeight: z.string().optional(),
  cardBg: z.string().min(1),
  tableBg: z.string().min(1),
  tableBgOpacity: z.number().min(0).max(1),
  tableHeaderBg: z.string().min(1),
  tableHeaderBgOpacity: z.number().optional(),
  tableHeaderColor: z.string().min(1),
  headerText: z.string().min(1),
  tableBorder: z.string().min(1),
  tableBorderOpacity: z.number().optional(),
  tableStripeColor1: z.string().optional(),
  tableStripeOpacity1: z.number().optional(),
  tableStripeColor2: z.string().optional(),
  tableStripeOpacity2: z.number().optional(),
  tableHoverColor: z.string().optional(),
  tableHoverOpacity: z.number().optional(),
  tableSelectedColor: z.string().optional(),
  tableSelectedOpacity: z.number().optional(),
  elevation: elevationConfigurationSchema,
  statusColors: statusColorsSchema.optional(),
  aesthetics: aestheticConfigurationSchema.optional(),
  table: tableConfigurationSchema.optional(),
  tokenMappings: tokenMappingsSchema.optional(),
}).passthrough();

const themeConfigurationSchema = z.object({
  primaryColor: z.string().min(1),
  secondaryColor: z.string().min(1),
  accentColor: z.string().min(1),
  darkMode: themeModeColorsSchema,
  lightMode: themeModeColorsSchema,
  borderRadius: z.string().min(1),
  typography: z.object({
    fontFamily: z.string().min(1),
    fontSize: z.object({
      base: z.string().min(1),
      sm: z.string().min(1),
      lg: z.string().min(1),
    }).passthrough(),
    lineHeight: z.string().min(1),
    tracking: z.object({
      tight: z.string().min(1),
      normal: z.string().min(1),
      wide: z.string().min(1),
    }).passthrough(),
    fontFeatureSettings: z.string().min(1),
  }).passthrough(),
  statusColors: statusColorsSchema,
  aesthetics: aestheticConfigurationSchema,
  table: tableConfigurationSchema,
  tokenMappings: tokenMappingsSchema,
}).passthrough();

export const frontendConfigurationSchema = z.object({
  api: apiConfigurationSchema,
  security: securityConfigurationSchema,
  circuitBreaker: circuitBreakerSchema.optional(),
  pagination: paginationConfigurationSchema.optional(),
  theme: themeConfigurationSchema,
  labels: z.record(z.string()),
  versions: z.object({
    api: z.number(),
    security: z.number(),
    theme: z.number(),
    labels: z.number(),
  }).passthrough(),
}).passthrough();

export type FrontendConfigurationSchemaType = z.infer<typeof frontendConfigurationSchema>;
